import { useState, useEffect, useRef } from 'react';
import { fetchSearch } from '../services/priceService';
import { logoTicker } from '../utils/format';
import './PortfolioAdd.css';

const DEFAULT_SUGGESTIONS = [
  { ticker: 'AAPL',  name: 'Apple Inc.',          type: 'stock' },
  { ticker: 'NVDA',  name: 'NVIDIA Corp.',         type: 'stock' },
  { ticker: 'TSLA',  name: 'Tesla, Inc.',          type: 'stock' },
  { ticker: 'BTC',   name: 'Bitcoin',              type: 'crypto' },
  { ticker: 'MSFT',  name: 'Microsoft Corp.',      type: 'stock' },
];

function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function TickerLogo({ ticker }) {
  const [failed, setFailed] = useState(false);
  if (failed) return <div className="pa-logo pa-logo--fallback">{ticker.slice(0, 3)}</div>;
  return (
    <div className="pa-logo">
      <img
        src={`https://assets.parqet.com/logos/symbol/${logoTicker(ticker)}`}
        alt={ticker}
        onError={() => setFailed(true)}
      />
    </div>
  );
}

function PortfolioAdd({ onAdd, onClose, existingTickers }) {
  const [query, setQuery]         = useState('');
  const [results, setResults]     = useState([]);
  const [searching, setSearching] = useState(false);
  const [error, setError]         = useState('');
  const [panelMaxH, setPanelMaxH] = useState('85vh');
  const inputRef = useRef(null);

  const debouncedQuery = useDebounce(query, 300);

  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 80);
  }, []);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => {
      const available = vv.height;
      setPanelMaxH(`${Math.round(available * 0.92)}px`);
    };
    update();
    vv.addEventListener('resize', update);
    return () => vv.removeEventListener('resize', update);
  }, []);

  useEffect(() => {
    if (!debouncedQuery.trim()) { setResults([]); return; }
    setSearching(true);
    fetchSearch(debouncedQuery)
      .then(data => { setResults(Array.isArray(data) ? data : []); setSearching(false); })
      .catch(() => { setResults([]); setSearching(false); });
  }, [debouncedQuery]);

  const handleAdd = (item) => {
    if (existingTickers.includes(item.ticker)) {
      setError(`${item.ticker} ist bereits in der Watchlist.`);
      return;
    }
    onAdd({ ticker: item.ticker, name: item.name, type: item.type });
    setError('');
  };

  const displayList = query.trim() ? results : DEFAULT_SUGGESTIONS;
  const isSearch = !!query.trim();

  return (
    <div className="pa-sheet" onClick={e => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div className="pa-panel" style={{ maxHeight: panelMaxH }}>
        <div className="pa-drag-handle" />

        <div className="pa-header">
          <span className="pa-title">Aktie hinzufügen</span>
        </div>

        <div className="pa-search-wrap">
          <span className="pa-search-icon">⌕</span>
          <input
            ref={inputRef}
            className="pa-input"
            type="text"
            placeholder="Ticker oder Name suchen…"
            value={query}
            onChange={e => { setQuery(e.target.value); setError(''); }}
            autoCapitalize="off"
            autoComplete="off"
          />
          {searching && <span className="pa-spinner">↻</span>}
          {query && !searching && (
            <button className="pa-clear" type="button" onClick={() => { setQuery(''); setResults([]); inputRef.current?.focus(); }}>✕</button>
          )}
        </div>

        {error && <p className="pa-error">{error}</p>}

        {!isSearch && (
          <p className="pa-section-label">Beliebt</p>
        )}

        <ul className="pa-results">
          {displayList.map(item => {
            const taken = existingTickers.includes(item.ticker);
            return (
              <li
                key={item.ticker}
                className={`pa-row ${taken ? 'pa-row--taken' : ''}`}
                onClick={() => !taken && handleAdd(item)}
              >
                <TickerLogo ticker={item.ticker} />
                <div className="pa-row-info">
                  <span className="pa-row-ticker">{item.ticker}</span>
                  <span className="pa-row-name">{item.name}</span>
                </div>
                {taken && <span className="pa-taken-check">✓</span>}
              </li>
            );
          })}
          {isSearch && !searching && results.length === 0 && (
            <li className="pa-empty">Keine Ergebnisse für „{query}"</li>
          )}
        </ul>
      </div>
    </div>
  );
}

export default PortfolioAdd;
